import { useCallback, useRef } from "react"

import type { InboxSelection } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { useThreadsMultiSelectState } from "./threads-multi-select-state"

/** The list behind the loaded rows; Cmd+A asks the server for the rest unless every row is loaded. */
export interface ThreadSelectionSource {
	args: InboxSelection
	isComplete: boolean
}

/** Every id the user meant: the loaded rows first, then the server's, without duplicates. */
export function mergeSelectedIds(
	loaded: readonly string[],
	fetched: readonly string[],
): string[] {
	return [...new Set([...loaded, ...fetched])]
}

export function useSelectAllThreads(
	allThreadIds: Array<string>,
	source: ThreadSelectionSource | undefined,
) {
	const bulkSelect = useThreadsMultiSelectState((state) => state.bulkSelect)
	const utils = api.useUtils()
	const argsKey = source ? JSON.stringify(source.args) : null
	const latestArgsKey = useRef(argsKey)
	latestArgsKey.current = argsKey

	return useCallback(() => {
		bulkSelect(allThreadIds)
		if (!source || source.isComplete) return
		const requested = useThreadsMultiSelectState.getState().threadIds
		const requestedFor = argsKey
		utils.thread.selectAll
			.fetch(source.args)
			.then((ids) => {
				// A selection change or another tab while loading wins over this result.
				if (useThreadsMultiSelectState.getState().threadIds !== requested)
					return
				if (latestArgsKey.current !== requestedFor) return
				bulkSelect(mergeSelectedIds(allThreadIds, ids))
			})
			.catch(() => {
				toast.error({ title: "Couldn't select every thread. Try again." })
			})
	}, [allThreadIds, source, argsKey, bulkSelect, utils])
}

// Text surfaces keep the native select-all: useShortcut skips form tags and
// contenteditable (Tiptap) unless a shortcut opts in.
export function useSelectAllThreadsShortcut(
	selectAll: () => void,
	enabled: boolean,
) {
	useShortcut(
		"meta+a, ctrl+a",
		(event) => {
			if (!enabled) return
			event.preventDefault()
			selectAll()
		},
		{ preventDefault: true },
		[selectAll, enabled],
	)
}
